const LG_LICENSE_KEY = 'D2E41CD6-D0DB-4ABE-935D-870B6624318A';
const LG_CLOSE_RESTORE_DELAY = 400;

class Gallery {
    constructor(args) {
        args = args || {};
        this.nocontrols = document.querySelectorAll('.nocontrol');
        this.scroll = args.scroll || null;
        this.navigation = args.navigation || null;
        this.update = args.update || null;
        this.galleryMode = 'layout2x';
        this.gallery = document.querySelector('.gallery__index');
        this.customScroll = document.querySelector('.custom-scrollbar');
        this.leftSide = document.querySelector('.gallery__leftPanel');
        this.galleryItems = document.querySelectorAll('.gallery__item');
        this.imageItems = document.querySelectorAll('.image-item');

        this.lg = document.getElementById('sidegallery');

        this.galleryInstance = null;
        this.destroyed = false;
        this.itemHandlers = [];
        this.restoreTimer = null;

        if (this.customScroll) {
            this.setTextOverflowHeight();
        }

        // lightGallery is ~110KB and only ever used on pages that render a
        // #sidegallery, so it is loaded on demand rather than in the main bundle.
        this.ready = this.lg ? this.initLightGallery() : Promise.resolve();
    }

    async initLightGallery() {
        const [{ default: lightGallery }, { default: lgVideo }, { default: lgVimeoThumbnail }] =
            await Promise.all([
                import(/* webpackChunkName: "lightgallery" */ 'lightgallery'),
                import(/* webpackChunkName: "lightgallery" */ 'lightgallery/plugins/video'),
                import(/* webpackChunkName: "lightgallery" */ 'lightgallery/plugins/vimeoThumbnail'),
            ]);

        // The page may have been navigated away from while the chunk loaded.
        if (this.destroyed || !this.lg) return;

        this.onBeforeOpen = () => {
            const content = document.querySelector('.lg-content');
            if (content) content.style.top = 0;
            this.navigation && this.navigation.reset();
            this.navigation && this.navigation.hideGalleryMenu();
            this.navigation && this.navigation.hideMainButton();
        };

        this.onAfterOpen = () => {
            clearTimeout(this.afterOpenTimer);
            this.afterOpenTimer = setTimeout(() => {
                this.closePreview();
            }, 1000);
        };

        this.onBeforeClose = () => {
            if (!this.nocontrols) this.navigation && this.navigation.showGalleryMenu();
            this.navigation && this.navigation.showMainButton();

            // Briefly un-fix <html> so lightGallery can restore scroll position,
            // then clear the inline style so the stylesheet's value applies again.
            // Hardcoding `fixed` here used to override the mobile breakpoint,
            // which expects `position: relative`.
            document.documentElement.style.position = 'relative';

            clearTimeout(this.restoreTimer);
            this.restoreTimer = setTimeout(() => {
                document.documentElement.style.position = '';
            }, LG_CLOSE_RESTORE_DELAY);
        };

        this.lg.addEventListener('lgBeforeOpen', this.onBeforeOpen);
        this.lg.addEventListener('lgAfterOpen', this.onAfterOpen);
        this.lg.addEventListener('lgBeforeClose', this.onBeforeClose);

        this.galleryInstance = lightGallery(this.lg, {
            licenseKey: LG_LICENSE_KEY,
            plugins: [lgVideo, lgVimeoThumbnail],
            speed: 500,
            mode: 'lg-fade',
            // `selector` is ignored when `dynamic` is enabled — items come from
            // dynamicEl below, and opening is driven by the click handlers.
            dynamic: true,
            dynamicEl: [...this.imageItems].map((item, i) => {
                return {
                    src: item.dataset['src'],
                    id: i,
                    subHtml: item.dataset['printdetails'] && item.dataset['printdetails'].replaceAll(',', '<br/>')
                };
            }),
            vimeoPlayerParams: {
                byline: 0,
                portrait: 0,
                color: 'A90707'
            },
            thumbnails: false,
            controls: true,
            loadVimeoThumbnail: true,
            vimeoThumbSize: "default"
        });

        this.galleryItems.forEach((item) => {
            const handler = () => {
                this.galleryInstance && this.galleryInstance.openGallery(parseInt(item.dataset.id));
            };

            item.addEventListener('click', handler);
            this.itemHandlers.push({ item, handler });
        });
    }

    setTextOverflowHeight() {
        if (!this.customScroll || !this.leftSide) return;
        this.customScroll.style.height = (this.leftSide.getBoundingClientRect().height - 280) + 'px';
    }

    setGalleryMode(mode) {
        if (mode === "layoutFull") {
            this.ready.then(() => {
                this.galleryInstance && this.galleryInstance.openGallery();
            });
        } else {
            if (!this.gallery) return;

            this.gallery.classList.add('fadeOut');

            clearTimeout(this.modeTimer);
            clearTimeout(this.modeFadeTimer);

            this.modeTimer = setTimeout(() => {
                this.gallery.className = 'gallery__index ' + mode;
            }, 200);

            this.modeFadeTimer = setTimeout(() => {
                this.gallery.classList.remove('fadeOut');
                this.scroll && this.scroll.scrollTo('.gallery');
            }, 300);
        }

        this.update && this.update();
    }

    onChange() {

    }

    destroy() {
        this.destroyed = true;

        clearTimeout(this.afterOpenTimer);
        clearTimeout(this.restoreTimer);
        clearTimeout(this.modeTimer);
        clearTimeout(this.modeFadeTimer);

        // Detach the actual handler references. The previous implementation
        // passed an unrelated method to removeEventListener, so nothing was
        // ever removed.
        this.itemHandlers.forEach(({ item, handler }) => {
            item.removeEventListener('click', handler);
        });
        this.itemHandlers = [];

        if (this.lg) {
            this.onBeforeOpen && this.lg.removeEventListener('lgBeforeOpen', this.onBeforeOpen);
            this.onAfterOpen && this.lg.removeEventListener('lgAfterOpen', this.onAfterOpen);
            this.onBeforeClose && this.lg.removeEventListener('lgBeforeClose', this.onBeforeClose);
        }

        // Pages without a #sidegallery never build an instance — calling
        // destroy() on undefined used to throw here, silently aborting the
        // rest of Barba's transition lifecycle.
        this.galleryInstance && this.galleryInstance.destroy();
        this.galleryInstance = null;
    }

    closeGallery() {
        this.navigation && this.navigation.hideGalleryMenu();
        this.galleryInstance && this.galleryInstance.closeGallery();
    }

    closePreview() {
        const currentlyHiddenItems = document.querySelectorAll('.hideGalleryItem');
        const currentlyOpenPanels = document.querySelectorAll('.preview-container');

        currentlyHiddenItems.forEach((item) => {
            item.classList.remove('hideGalleryItem');
        });

        currentlyOpenPanels.forEach((item) => {
            item.classList.remove('in');
            item.remove();
        });

        return Promise.resolve();
    }
}

export default Gallery;
